import { useTranslation } from 'react-i18next';

import type { AccessGroup } from '@/api/auth';
import { Button } from '@/components/ui/Button';

import { mayWriteVacancies } from './vacancyAccess';

interface Props {
  groups: AccessGroup[];
  /**
   * What opening the dialog does — §6.8's **New vacancy** dialog, which is **TASK-072's**. It stays
   * optional because the group check, not the dialog, is what this component exists for: this screen is
   * read-only in TASK-071, and the empty state still has to carry §8's one action for the group that
   * can act on it. The same seam Sprint 9's `AddCandidateButton` left for §6.4's dialog, for the same
   * reason and in the same shape.
   */
  onClick?: () => void;
  className?: string;
}

/**
 * §6.8's **New vacancy**, in the one place TASK-071 puts it: the action of the *"No vacancies yet."*
 * empty state. TASK-072 adds the second place — above the grid — and the dialog behind both.
 *
 * ⚠️ **Not rendered for a Recruiter — hidden, never disabled** (§5, and `vacancyAccess.ts` cites the
 * two matrix rows that refuse them). The group check lives in this component so that both call sites
 * inherit it rather than each remembering: an empty state whose action answers 403 is worse than an
 * empty state with no action, and a *disabled* one tells a Recruiter that the capability exists and is
 * being withheld.
 */
export function NewVacancyButton({ groups, onClick, className }: Props) {
  const { t } = useTranslation('vacancies');

  if (!mayWriteVacancies(groups)) return null;

  return (
    <Button onClick={onClick} className={className}>
      {t('add')}
    </Button>
  );
}
